import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { OrgData } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import OrgHeadBox from "./components/OrgHeadBox";
import OrgExecGrid from "./components/OrgExecGrid";
import OrgCommColumns from "./components/OrgCommColumns";
import OrgEtcRow from "./components/OrgEtcRow";

export default function OrgPage() {
  const { data, loading, error, reload } = useApi<OrgData>("org.php");

  // head/exec/comm/etc 는 서버가 이미 나눠서 내려준다. 프런트에서 filter 하지 않는다.
  const head = data?.head ?? [];
  const exec = data?.exec ?? [];
  const comm = data?.comm ?? [];
  const etc = data?.etc ?? [];

  const isEmpty =
    !loading &&
    !error &&
    head.length === 0 &&
    exec.length === 0 &&
    comm.length === 0 &&
    etc.length === 0;

  const handlePrint = () => window.print();

  return (
    <div className="org-page">
      <PageBanner
        title="조직도"
        description="2026-27 대전민들레로타리클럽의 임원과 위원회 조직을 소개합니다."
        breadcrumb={[{ label: "클럽소개", to: "/about" }, { label: "조직도" }]}
      />

      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        {/* 상단 타이틀 + 인쇄 버튼 */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {data?.title || "클럽 조직도"}
            </h2>
            {data?.year && (
              <p className="mt-1 text-sm text-foreground-500">{data.year}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handlePrint}
            className="no-print inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-printer-line" aria-hidden="true"></i>
            인쇄
          </button>
        </div>

        {loading ? (
          <div className="space-y-6">
            <Skeleton className="mx-auto h-32 w-full max-w-sm rounded-xl" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="조직도를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : isEmpty ? (
          <EmptyState icon="ri-node-tree" title="등록된 조직 정보가 없습니다" />
        ) : (
          <div className="org-tiers">
            {head.length > 0 && <OrgHeadBox group={head[0]} />}

            {(exec.length > 0 || comm.length > 0) && (
              <span className="org-connector" aria-hidden="true"></span>
            )}
            {exec.length > 0 && <OrgExecGrid groups={exec} />}

            {comm.length > 0 && (
              <>
                <span className="org-connector" aria-hidden="true"></span>
                <OrgCommColumns groups={comm} />
              </>
            )}

            {etc.length > 0 && <OrgEtcRow groups={etc} />}
          </div>
        )}
      </section>
    </div>
  );
}